<template>
    <div>
        <TreeTable
            :value="routeTree"
            :filters="filters"
            :expanded-keys="expandedKeys"
            :filterMode="'lenient'"
            tableStyle="min-width: 50rem"
            show-gridlines
            :autoLayout="true"
            :resizableColumns="true"
            :row-hover="true"
            @filter="handleFilter"
        >
            <Column
                field="name"
                header="Name"
                filter-match-mode="contains"
                expander
            >
                <template #filter>
                    <div class="filter">
                        <InputText
                            v-model="filters['name']"
                            type="text"
                            placeholder="Filter"
                        />
                    </div>
                </template>
            </Column>
            <Column
                field="method"
                header="Method"
            >
                <template #filter>
                    <div class="filter">
                        <Select
                            v-model="filters['method']"
                            :options="methods"
                            optionLabel="name"
                            optionValue="value"
                            display="chip"
                            showClear
                            placeholder="Filter"
                        >
                            <template #value="slotProps">
                                <Chip
                                    v-if="slotProps.value"
                                    :label="slotProps.value"
                                    :pt:root:style:backgroundColor="httpMethodColors[slotProps.value]"
                                    pt:root:style:color="white"
                                />
                            </template>
                            <template #option="slotProps">
                                <div class="flex items-center">
                                    <div :style="{ color: httpMethodColors[slotProps.option.value] }">
                                        {{ slotProps.option.name }}
                                    </div>
                                </div>
                            </template>
                        </Select>
                    </div>
                </template>
                <template #body="slotProps">
                    <div
                        v-if="slotProps.node.data.method"
                        class="cell-array-container"
                    >
                        <Chip
                            v-for="(method, methodIndex) in slotProps.node.data.method"
                            :key="methodIndex"
                            :label="method"
                            :pt:root:style:backgroundColor="httpMethodColors[method]"
                            pt:root:style:color="white"
                        />
                    </div>
                </template>
            </Column>
            <Column
                field="is_private"
                header="Private"
            >
                <template #filter>
                    <div class="filter">
                        <SelectButton
                            v-model="privateFilterValue"
                            :options="['Yes', 'No']"
                            @change="handleIsPrivateFilter"
                        />
                    </div>
                </template>
                <template #body="slotProps">
                    <div
                        v-if="slotProps.node.data.is_private"
                        class="cell-array-container"
                    >
                        ✅
                    </div>
                </template>
            </Column>
            <Column
                :field="permissionFileTypeKey"
                header="Permissions"
                filter-match-mode="contains"
            >
                <template #filter>
                    <div class="filter">
                        <Select
                            v-model="filters[permissionFileTypeKey]"
                            :options="index['permissions']"
                            optionLabel="name"
                            optionValue="value"
                            display="chip"
                            filter
                            showClear
                            placeholder="Filter"
                        ></Select>
                        <SelectButton
                            v-model="permissionTypeFilterValue"
                            size="small"
                            :options="['Roles', 'Mobile Features']"
                        />
                    </div>
                </template>
                <template #body="slotProps">
                    <div
                        v-if="slotProps.node.data[permissionFileTypeKey]"
                        class="cell-array-container"
                    >
                        <!-- eslint-disable vue/no-v-for-template-key -->
                        <template
                            v-for="(permission, permissionIndex) in slotProps.node.data[permissionFileTypeKey]"
                            :key="permissionIndex"
                        >
                            <!-- eslint-enable -->
                            <Chip
                                v-if="
                                    slotProps.node.data.mobileFeaturePermissions.length === 0 ||
                                    !slotProps.node.data.mobileFeaturePermissions.includes(permission)
                                "
                                :label="permission"
                            />
                            <Chip
                                v-else
                                :label="permission"
                                pt:root:style:backgroundColor="yellow"
                            />
                        </template>
                    </div>
                </template>
            </Column>
            <Column
                field="organization_features"
                header="Portal Features"
                filter-match-mode="contains"
            >
                <template #filter>
                    <div class="filter">
                        <Select
                            v-model="filters['organization_features']"
                            :options="index['organization_features']"
                            optionLabel="name"
                            optionValue="value"
                            display="chip"
                            showClear
                            placeholder="Filter"
                        ></Select>
                    </div>
                </template>
                <template #body="slotProps">
                    <div
                        v-if="slotProps.node.data.organization_features"
                        class="cell-array-container"
                    >
                        <Chip
                            v-for="(organization_feature, featureIndex) in slotProps.node.data.organization_features"
                            :key="featureIndex"
                            :label="organization_feature"
                        />
                    </div>
                </template>
            </Column>
        </TreeTable>
    </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import TreeTable from 'primevue/treetable'
import Chip from 'primevue/chip'
import Column from 'primevue/column'
import SelectButton from 'primevue/selectbutton'
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'

export default {
    components: {
        Chip,
        Column,
        // eslint-disable-next-line vue/no-unused-components
        MultiSelect,
        // eslint-disable-next-line vue/no-reserved-component-names
        Select,
        SelectButton,
        InputText,
        TreeTable,
    },

    data() {
        return {
            routes: null,
            routeTree: null,
            index: {},
            filters: {},
            expandedKeys: [],
            httpMethodColors: {
                GET: '#10B981',
                DELETE: '#F43F5E',
                POST: '#0EA5E9',
                PUT: '#F59E0B',
                PATCH: '#8B5CF6',
                HEAD: '#64748B',
                OPTIONS: '#14B8A6',
            },
            methods: [
                {
                    name: 'GET',
                    value: 'GET',
                    color: '#10B981',
                },
                {
                    name: 'DELETE',
                    value: 'DELETE',
                    color: '#F43F5E',
                },
                {
                    name: 'POST',
                    value: 'POST',
                    color: '#0EA5E9',
                },
                {
                    name: 'PUT',
                    value: 'PUT',
                    color: '#F59E0B',
                },
                {
                    name: 'PATCH',
                    value: 'PATCH',
                    color: '#8B5CF6',
                },
                {
                    name: 'HEAD',
                    value: 'HEAD',
                    color: '#64748B',
                },
                {
                    name: 'OPTIONS',
                    value: 'OPTIONS',
                    color: '#14B8A6',
                },
            ],
            privateFilterOptions: [true, false],
            privateFilterValue: null,
            permissionTypeFilterValue: null,
        }
    },

    computed: {
        permissionFileTypeKey() {
            return this.permissionTypeFilterKeyForValue(this.permissionTypeFilterValue)
        },
    },

    watch: {
        permissionTypeFilterValue(newValue, oldValue) {
            const filters = this.filters[this.permissionTypeFilterKeyForValue(oldValue)]
            this.filters[this.permissionTypeFilterKeyForValue(newValue)] = filters
            delete this.filters[this.permissionTypeFilterKeyForValue(oldValue)]
        },
    },

    mounted() {
        this.getRoutes()
    },

    methods: {
        getRoutes() {
            this.$http.get(`/developers/routes`).then((response) => {
                this.routeTree = response.data.tree

                this.index.permissions = response.data.index.permissions.map((item) => {
                    return {
                        name: item,
                        value: item,
                    }
                })

                this.index.organization_features = response.data.index.organization_features.map((item) => {
                    return {
                        name: item,
                        value: item,
                    }
                })
            })
        },
        handleFilter({ filters, filteredValue }) {
            this.expandedKeys = []

            if (
                filteredValue.length &&
                (filters.name ||
                    (filters.simplePermissions && filters.simplePermissions.length) ||
                    (filters.allPermissions && filters.allPermissions.length) ||
                    (filters.mobileFeaturePermissions && filters.mobileFeaturePermissions.length) ||
                    (filters.organization_features && filters.organization_features.length) ||
                    filters.method ||
                    filters.is_private === true ||
                    filters.is_private === false)
            ) {
                this.getKeysFromNode(filteredValue).forEach((key) => {
                    this.expandedKeys[key] = true
                })
            }
        },

        handleIsPrivateFilter({ value }) {
            if (value === 'Yes') {
                this.filters.is_private = true
            } else if (value === 'No') {
                this.filters.is_private = false
            } else {
                this.filters.is_private = undefined
            }
        },

        getKeysFromNode(nodes) {
            const keys = []

            function traverse(node) {
                keys.push(node.key)
                if (node.children) {
                    node.children.forEach(traverse)
                }
            }

            nodes.forEach(traverse)
            return keys
        },

        permissionTypeFilterKeyForValue(key) {
            if (key === 'Roles') {
                return 'simplePermissions'
            }

            if (key === 'Mobile Features') {
                return 'mobileFeaturePermissions'
            }

            return 'allPermissions'
        },
    },
}
</script>

<style scoped>
:deep(.rollup) td:first-child {
    //background-color: #9e9e9e;
    font-style: italic;
}

.cell-array-container {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
}

.filter {
    display: flex;
    flex-direction: column;
}
</style>
